import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectProps,
  Stack,
  Tooltip,
} from '@mui/material';
import { forwardRef, ReactNode, useCallback, useMemo } from 'react';
import { InfoTooltip } from '../Tooltip';
import { FlexRow } from '../Layout';
import { FormControlWrapper, FormLabelWrapper, HelperText } from './Components';

export interface MenuItem {
  value: any;
  name: string;
  description?: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

interface Props
  extends Pick<
    SelectProps,
    | 'inputProps'
    | 'fullWidth'
    | 'size'
    | 'onChange'
    | 'onBlur'
    | 'name'
    | 'value'
    | 'disabled'
    | 'required'
    | 'multiple'
    | 'renderValue'
    | 'defaultValue'
    | 'open'
    | 'onOpen'
    | 'onClose'
  > {
  menuItems: MenuItem[];
  label?: string;
  labelDirection?: 'row' | 'column';
  reset?: () => void;
  success?: string;
  error?: string;
  tooltipText?: string;
  width?: number;
  placeholder?: string;
  margin?: 'normal' | 'dense' | 'none';
  noOptionsText?: string;
  labelWidth?: number;
  menuItemHasSpaceBetween?: boolean;
  renderOption?: (menuItem: MenuItem) => ReactNode;
  minWidth?: number;
  limitTags?: number;
  fixedHeight?: boolean;
}

const SelectInputField = forwardRef(
  (
    {
      label,
      required,
      menuItems,
      error,
      placeholder,
      margin = 'dense',
      inputProps,
      fullWidth = false,
      disabled,
      tooltipText,
      size = 'medium',
      reset,
      labelDirection = 'column',
      success,
      multiple,
      width,
      noOptionsText,
      menuItemHasSpaceBetween = true,
      labelWidth = 100,
      renderOption,
      minWidth = 140,
      limitTags,
      fixedHeight = false,
      ...rest
    }: Props,
    ref
  ) => {
    const inputId = `${rest.name}-id`;
    const formLabelId = `${rest.name}-label`;
    const helperTextId = `${rest.name}-helperText`;

    const renderMultipleValues = useCallback(
      (selected: any) => {
        if (!Array.isArray(selected)) return selected || placeholder || '';

        const filteredArray: string[] = [];
        selected.forEach((selectedItem: string) => {
          const result = menuItems.find((item) => item.value === selectedItem);
          if (result) {
            filteredArray.push(result.name);
          }
        });

        if (filteredArray.length === 0) return placeholder || '';

        // If limitTags is set, show only the first N items and "+X" for the rest
        if (limitTags !== undefined && filteredArray.length > limitTags) {
          const visibleItems = filteredArray.slice(0, limitTags);
          const remainingCount = filteredArray.length - limitTags;
          return `${visibleItems.join(', ')} +${remainingCount}`;
        }

        return filteredArray.join(', ');
      },
      [menuItems, placeholder, limitTags]
    );

    const renderValue = useMemo(() => {
      if (multiple) {
        return renderMultipleValues;
      }
      if (placeholder && ['', null, undefined].some((val) => val === rest.value)) {
        return () => (
          <Box component="span" color={({ palette }) => palette.grey[400]}>
            {placeholder}
          </Box>
        );
      }
      return undefined;
    }, [multiple, placeholder, renderMultipleValues, rest.value]);

    return (
      <FormControl
        sx={{ minWidth }}
        margin={margin}
        error={!!error}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        component={FormControlWrapper}
        labelDirection={labelDirection}
      >
        {label && (
          <FormLabelWrapper direction={labelDirection} width={labelWidth} size={size}>
            <FormLabel htmlFor={inputId} id={formLabelId}>
              {label}
            </FormLabel>
            {tooltipText && <InfoTooltip tooltipText={tooltipText} disabled={disabled} />}
          </FormLabelWrapper>
        )}

        <Stack>
          <Select
            style={{ ...(!fullWidth && width && { width: width }) }}
            size={size}
            key={inputId}
            inputRef={ref}
            id={inputId}
            labelId={formLabelId}
            aria-describedby={helperTextId}
            aria-label={label}
            displayEmpty
            fullWidth={fullWidth}
            multiple={multiple}
            renderValue={renderValue}
            sx={
              fixedHeight
                ? {
                    '& .MuiSelect-select': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }
                : undefined
            }
            {...rest}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {placeholder && (
              <MenuItem disabled value="" sx={{ my: 2 }}>
                {placeholder}
              </MenuItem>
            )}

            {menuItems.length > 0 ? (
              menuItems.map(({ value: optionValue, name, description, disabled, tooltip }) => {
                if (renderOption) return renderOption({ name, value: optionValue, description });

                const menuItemContent = (
                  <MenuItem
                    key={`${inputId}-${optionValue}`}
                    value={optionValue}
                    dense
                    disabled={disabled}
                    sx={{
                      justifyContent: multiple
                        ? 'flex-start'
                        : menuItemHasSpaceBetween
                        ? 'space-between'
                        : 'initial',
                      gap: multiple ? 1 : undefined,
                    }}
                  >
                    {multiple && (
                      <Checkbox
                        size="small"
                        checked={Array.isArray(rest.value) && rest.value.includes(optionValue)}
                        sx={{ my: '-12px' }}
                      />
                    )}
                    <FlexRow gap={2}>
                      {name} {description}
                    </FlexRow>
                  </MenuItem>
                );

                if (disabled && tooltip) {
                  return (
                    <Tooltip key={`${inputId}-${optionValue}`} title={tooltip} arrow>
                      <span>{menuItemContent}</span>
                    </Tooltip>
                  );
                }

                return menuItemContent;
              })
            ) : (
              <MenuItem value="" dense disabled>
                {noOptionsText}
              </MenuItem>
            )}
          </Select>

          {error && (
            <HelperText id={helperTextId} status={'error'}>
              {error}
            </HelperText>
          )}

          {success && (
            <HelperText id={helperTextId} status={'success'}>
              {success}
            </HelperText>
          )}
        </Stack>
      </FormControl>
    );
  }
);

export default SelectInputField;
